import React, { ReactElement, useState, useEffect } from "react";

import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { i18n } from "../../i18n";

import { useFormik } from "formik";

import * as Yup from "yup";
import Select from "react-select";

import {
  signInWithEmail,
  onSnapshotRegistrationSettings,
} from "../../apis/firebase";
import apiRequest from "../../apis/request";

import { useSelector, shallowEqual } from "react-redux";

let snapshotRef = null;

const PreSignup = (): ReactElement => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");

  const [customFields, setCustomField] = useState([]);

  const { generalSettings } = useSelector(
    ({ generalSettings }) => ({ generalSettings }),
    shallowEqual
  );

  useEffect(() => {
    snapshotRef = onSnapshotRegistrationSettings((dt) => {
      const { customFields: _customFields } = { ...dt };
      setCustomField(_customFields || []);
    });

    return () => snapshotRef();
  }, []);

  const { t } = useTranslation();

  const search = window.location.search;
  const query = new URLSearchParams(search);

  const tId = query.get("t") || "";

  const initialValues = {
    email: "",
    firstName: "",
    lastName: "",
    admin: false,
  };

  Yup.addMethod(Yup.string, "emailDomainCheck", function (errorMessage) {
    return this.test(`test-email-domain`, errorMessage, function (value) {
      if (
        !generalSettings.authorisedDomainName ||
        generalSettings.authorisedDomainName === "" ||
        !value
      )
        return true;

      const domains = generalSettings.authorisedDomainName.split(",");
      let _flag = false;

      domains.forEach((domain) => {
        if (value.endsWith(domain.trim())) {
          _flag = true;
          return;
        }
      });

      return _flag;
    });
  });

  const formValidatorObject = {
    email: Yup.string()
      .email(t("FORM_EMAIL_VALIDATION_FORMAT"))
      .required(t("FORM_REQUIRED_FIELD"))
      .emailDomainCheck(t("FORM_EMAIL_VALIDATION_DOMAIN")),
    firstName: Yup.string().required(t("FORM_REQUIRED_FIELD")),
    lastName: Yup.string().required(t("FORM_REQUIRED_FIELD")),
  };

  if (!!customFields?.length) {
    customFields.forEach((d) => {
      initialValues[d.fieldName] = "";
      let _req: any;
      switch (d.type) {
        case "text":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "checkbox":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "file":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "select":
          if (d.selectSize > 1) {
            _req = Yup.array().of(
              Yup.string().required(t("FORM_REQUIRED_FIELD"))
            );
          } else if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
      }
      formValidatorObject[d.fieldName] = _req;
    });
  }

  const FormSchema = Yup.object().shape(formValidatorObject);

  const formik = useFormik({
    initialValues,
    validationSchema: FormSchema,
    onSubmit: async (values) => {
      try {
        values["trackId"] = tId;
        values["lang"] = i18n.language;
        // insertPreRegisterUser(values);
        const resp = await apiRequest(
          "POST",
          "/users/preEventSignup",
          [],
          values
        );

        if (resp.status === 200) {
          const data: any = resp.data;
          setTimeout(async () => {
            await signInWithEmail(data.user.email, data.user.participantId);
          }, 5000);

          window.localStorage.setItem("emailForSignIn", values.email);
          setIsSubmitted(true);
        } else {
          setFormErrorMessage(t("WRONG_REGISTRATION_URL"));
        }
      } catch (e) {
        if (e === "auth/email-already-in-use") {
          setFormErrorMessage(t("ERROR_REGISTER_EMAIL_ALREADY_EXIST"));
        }
        if (e === "auth/weak-password") {
          setFormErrorMessage(t("ERROR_REGISTER_CHOOSE_STRONG_PASSWORD"));
        }
      }
    },
  });

  return (
    <div className="d-flex flex-column mb-4">
      {!!isSubmitted && (
        <div className="mb-3">
          <div
            dangerouslySetInnerHTML={{
              __html: t("REGISTER_SUCCESS_EMAIL_SENT"),
            }}
          />
        </div>
      )}
      {!isSubmitted && (
        <>
          <div className="mb-3">
            <div className="d-flex align-items-start justify-content-between">
              <h1 className="mb-2">{t("REGISTER_TITLE")}</h1>
            </div>
            <p className="m-0">{t("REGISTER_DESCRIPTION")}</p>
          </div>
          <form onSubmit={formik.handleSubmit}>
            {formErrorMessage && (
              <div className="alert alert-danger py-1 px-2 fs-7" role="alert">
                <i className="bi bi-exclamation-circle-fill me-1"></i>
                {formErrorMessage}
              </div>
            )}
            <div className="mb-3">
              <input
                type="text"
                name="lastName"
                className={`form-control ${
                  formik.errors.lastName ? "is-invalid" : ""
                }`}
                placeholder={t("FORM_LAST_NAME")}
                {...formik.getFieldProps("lastName")}
              />
              {formik.touched.lastName && formik.errors.lastName ? (
                <div className="invalid-feedback error-block">
                  {formik.errors.lastName}
                </div>
              ) : null}
            </div>

            <div className="mb-3">
              <input
                type="text"
                name="firstName"
                className={`form-control ${
                  formik.errors.firstName ? "is-invalid" : ""
                }`}
                placeholder={t("FORM_FIRST_NAME")}
                {...formik.getFieldProps("firstName")}
              />
              {formik.touched.firstName && formik.errors.firstName ? (
                <div className="invalid-feedback error-block">
                  {formik.errors.firstName}
                </div>
              ) : null}
            </div>

            <div className="mb-3">
              <input
                type="email"
                name="email"
                className={`form-control ${
                  formik.errors.email ? "is-invalid" : ""
                }`}
                placeholder={t("FORM_EMAIL_PLACEHOLDER")}
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="invalid-feedback error-block">
                  {formik.errors.email}
                </div>
              ) : null}
            </div>

            {!!customFields?.length &&
              customFields.map((d, i) => {
                const _type = d.validationCriteria.emailField
                  ? "email"
                  : d.type;

                return (
                  <div key={i} className="mb-3">
                    <div className="input-group ">
                      {(d.type === "text" ||
                        d.type === "number" ||
                        d.type === "email") && (
                        <>
                          <input
                            type={_type}
                            placeholder={
                              d.label_en || d.label_fr
                                ? d[`label_${i18n.language}`]
                                : d.label
                            }
                            className={`form-control ${
                              formik.errors[`${d.fieldName}`]
                                ? "is-invalid"
                                : ""
                            }`}
                            {...formik.getFieldProps(`${d.fieldName}`)}
                          />
                        </>
                      )}

                      {d.type === "file" && (
                        <>
                          <input
                            type={d.type}
                            id="files"
                            className={`form-control  ${
                              formik.errors[`${d.fieldName}`]
                                ? "is-invalid"
                                : ""
                            }`}
                            onChange={(event) => {
                              const files = event.target.files[0];
                              formik.setFieldValue(`${d.fieldName}`, files);
                            }}
                          />
                        </>
                      )}

                      {d.type === "checkbox" && (
                        <div className="d-flex flex-wrap align-items-center ">
                          <input
                            type={d.type}
                            placeholder={d.placeholder}
                            className={`form-check-input  ${
                              formik.errors[`${d.fieldName}`]
                                ? "is-invalid"
                                : ""
                            }`}
                            {...formik.getFieldProps(`${d.fieldName}`)}
                          />
                          <label className="fs-7 form-check-label ms-2">
                            {d.label}
                          </label>
                        </div>
                      )}

                      {d.type === "select" &&
                        (!d.selectSize || d.selectSize === 1) && (
                          <Select
                            className={`custom-select w-100 ${
                              formik.errors[`${d.fieldName}`]
                                ? "is-invalid"
                                : ""
                            }`}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            options={d.options}
                            placeholder={
                              d.label_en || d.label_fr
                                ? d[`label_${i18n.language}`]
                                : d.label
                            }
                            onChange={(value) => {
                              formik.setFieldValue(
                                `${d.fieldName}`,
                                value?.value
                              );
                            }}
                          />
                        )}

                      {d.type === "select" &&
                        (!d.selectSize || d.selectSize > 1) && (
                          <Select
                            className={`custom-select col-12 ${
                              formik.errors[`${d.fieldName}`]
                                ? "is-invalid"
                                : ""
                            }`}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            options={d.options}
                            isMulti
                            placeholder={
                              d.label_en || d.label_fr
                                ? d[`label_${i18n.language}`]
                                : d.label
                            }
                            onChange={(value) => {
                              formik.setFieldValue(
                                `${d.fieldName}`,
                                value?.map((d) => d?.value)
                              );
                            }}
                          />
                        )}
                      {formik.errors[`${d.fieldName}`] ? (
                        <div className="invalid-feedback error-block">
                          {formik.errors[`${d.fieldName}`]}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}

            <div className="mt-2">
              <button type="submit" className="btn btn-primary fw-bold w-100">
                {t("FORM_BUTTON_REGISTER_LABEL")}
              </button>
            </div>
            <div className="mt-4">
              <Link to="/login" className="btn btn-secondary fw-bold w-100">
                {t("FORM_BUTTON_LOGIN_LABEL")}
              </Link>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default PreSignup;
